import { MediaQuery } from "@outplayed/responsive";
import { TooltipContainer } from "@outplayed/tooltips";
import { HeaderPatch } from "@ugg/shared/components/common/HeaderPatch";
import { asArray } from "@ugg/shared/utils/safe-coerce";
import { getTierGradeColorHex, type TierNameValue } from "@ugg/shared/utils/tier-helpers";
import { getClassicChampionImgUrl, getClassicChampionSkillImgUrl, type ClassicChampionData } from "@ugg/shared/hooks/classic";
import { SkillTooltipContent, formatCooldown } from "./lib/skill-tooltip";

// Part of the League Classic build page. See ./ClassicChampionProfile.tsx for
// the module boundary rules: nothing outside this folder imports from here
// except ./index.ts, and nothing in here is hoisted into shared component
// directories — that is what keeps Classic deletable as one unit.

export function ClassicHeader(props: {
  championKey?: string;
  name: string;
  champion?: ClassicChampionData;
  roleLabel: string;
  // Display patch ("26.15") for the header badge — NOT the asset path segment.
  patch: string;
  // Resolved <version> for the versioned Classic asset CDN ("16_15"). Undefined
  // until the page's patch resolves, in which case the images are skipped
  // rather than requested against an incomplete URL.
  assetVersion?: string;
  tier?: TierNameValue;
}) {
  const { championKey, name, champion, roleLabel, patch, assetVersion, tier } = props;

  const skillSlots: Array<{ key: "p" | "q" | "w" | "e" | "r"; label: string }> = [
    { key: "p", label: "P" },
    { key: "q", label: "Q" },
    { key: "w", label: "W" },
    { key: "e", label: "E" },
    { key: "r", label: "R" },
  ];

  const spellByKey = (key: string) => asArray(champion?.spells).find((s) => s.spellKey === key);

  // Tier-specific portrait border + badge color, mirroring the normal build
  // page's ChampionIconContainer (tierColor on both borders, white badge text).
  // getTierGradeColorHex maps the letter grade → the shared TIER_GRADE_STYLES
  // color (S+ orange, S blue, …); falls back to the neutral frame until a tier
  // resolves / when the champion has no tier-list entry.
  const tierColor = tier ? getTierGradeColorHex(tier) : "#414165";

  // SEO copy — rendered twice (like the normal champion header): inline beside
  // the skill icons on tablet/desktop, and again below the whole header on
  // mobile (small screens), so mobile still gets the crawlable text.
  const seoText = `U.GG ${name} Classic build shows best ${name} Classic runes and masteries by the best players. With skill order and items, this ${name} guide offers a full LoL ${name} Classic build for Patch ${patch}`;

  return (
    <div className="mb-[24px]">
      <div className="flex items-start w-full max-sm:relative">
        {/* Portrait frame mirrors the normal header's ChampionIconContainer
            (93px, tier-colored border + centered tier badge). Tier comes from the
            Classic tier list (the selected role's tier, or the core-role tier as
            a fallback); "?" until it resolves / if there's no tier-list entry. */}
        <div
          className="relative flex-none w-[93px] h-[93px] rounded-[6px] border-[2px] bg-[#17172e]"
          style={{ borderColor: tierColor }}
        >
          <div
            className="absolute z-[2] top-[-16px] left-[50%] translate-x-[-50%] flex items-center justify-center w-[36px] h-[20px] rounded-[4px] border-[1px] bg-[#06061f] text-white text-[11px] font-bold"
            style={{ borderColor: tierColor }}
          >
            {tier || "?"}
          </div>
          <div className="relative w-full h-full rounded-[4px] border-[2px] border-[#17172e] overflow-hidden">
            <div className="absolute top-[-6px] left-[50%] z-[1] translate-x-[-50%] w-[40px] h-[10px] rounded-b-[2px] border-[2px] border-purple-500 bg-[#06061f]" />
            {championKey && assetVersion && (
              <img
                src={getClassicChampionImgUrl(championKey, assetVersion)}
                alt={name}
                className="w-full h-full rounded-[2px] scale-[1.2]"
              />
            )}
          </div>
        </div>
        <div className="flex-1 flex flex-col justify-between min-w-0 h-full ml-[24px] max-sm:ml-[12px] max-sm:self-end">
          {/* Mirrors the normal champion header exactly: min-md:whitespace-nowrap
              + max-sm:block so the title wraps/shrinks on mobile, and
              max-sm:mt-[-100px] (with the column's self-end + the 93px portrait)
              pulls the title up beside the portrait like the other pages. */}
          <h1 className="flex min-md:whitespace-nowrap items-center max-sm:items-start font-['Barlow'] max-sm:leading-[0.7] max-sm:mt-[-100px] max-sm:block">
            <span className="mr-[12px] text-[36px] font-semibold max-sm:text-[24px] max-sm:mr-[6px]">{name}</span>
            <span className="text-[36px] font-normal text-lavender-50 max-sm:w-full max-sm:text-[20px]">
              Classic Build for {roleLabel}
            </span>
            {patch && (
              <MediaQuery min="TABLET" max="DESKTOP_LARGE">
                <HeaderPatch className="ml-[10px]" patch={patch} />
              </MediaQuery>
            )}
          </h1>
          <div className="flex items-center h-[32px] mt-[18px] max-sm:mt-[12px]">
            <div className="flex-none flex items-center mr-[24px] gap-[6px]">
              {skillSlots.map(({ key, label }) => {
                const isPassive = key === "p";
                const spell = spellByKey(key);
                const tipName = isPassive ? champion?.passive?.name : spell?.name;
                return (
                  <TooltipContainer
                    key={key}
                    disableTooltip={!champion}
                    tooltipInfo={
                      <SkillTooltipContent
                        name={tipName || label}
                        cooldown={formatCooldown(spell?.cooldownCoefficients)}
                        description={isPassive ? champion?.passive?.description : spell?.dynamicDescription}
                        isPassive={isPassive}
                      />
                    }
                  >
                    <div className="relative">
                      {championKey && assetVersion && (
                        <img
                          src={getClassicChampionSkillImgUrl(championKey, key, assetVersion)}
                          alt={tipName || label}
                          className="w-[36px] h-[36px] rounded-[3px]"
                        />
                      )}
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 flex items-center justify-center w-[12px] h-[12px] rounded-full border-[2px] border-[#17172e] bg-[#383864] text-[9px] font-bold leading-none">
                        {label}
                      </div>
                    </div>
                  </TooltipContainer>
                );
              })}
            </div>
            {/* Inline SEO — tablet/desktop only (hidden on mobile; shown below). */}
            <div className="pr-[12px] text-lavender-50 text-[12px] max-sm:text-[11px] font-medium max-xs:hidden">{seoText}</div>
          </div>
        </div>
      </div>
      {/* Mobile SEO — under the portrait/skills (matches the normal champion
          header's second SEO block); the inline copy above is hidden here. */}
      <div className="mt-[24px] pr-[12px] max-sm:mt-[18px] text-lavender-50 text-[11px] font-medium xs:hidden">{seoText}</div>
    </div>
  );
}

// ─── Filter bar (stripped: role always visible; queue/patch/worlds under More) ─

// Filter bar — mirrors the site's FilterManager markup (wrapper + funnel label)
// so it reads identically to the normal build page. Everything is shown inline
// (no More/Less). Role is the site's 5-button role split (crawlable role Links,
// matching the normal build page); Queue is locked to Classic (only mode on
// this route); Patch is the real version selector (same window as the Classic
// tier list, driven by the `?patch=` query param); Region is Worlds-only
// (reusing REGION_OPTIONS_ONLY_WORLD so it renders the same globe as elsewhere).
